/**
 * Единственный источник СОСТАВА группы «Настройки» в сайдбаре.
 *
 * Раньше состав дублировался в `menuConfig.tsx` и `menuConfigExtended.tsx`; живым в
 * рендере является только extended, поэтому расхождение было бы невидимым до приёмки.
 * Здесь список один, оба конфига раскладывают его своим `getItem`.
 *
 * ГЕЙТЫ ЗДЕСЬ НЕ ЖИВУТ. Они приходят из единого реестра
 * `shared/config/settingsGates.ts`, откуда их читает и реестр карточек хаба
 * (`features/settings/config/settingsHubCards.tsx`). До 2026-07-26 каноном
 * числился реестр карточек, а гейты дублировались литералами в обоих местах —
 * и разошлись: у карточек «Cron/Планировщик», «Биллинг» и «Задачи» гейта не было
 * вовсе при загейченных пунктах меню. Один источник вместо двух согласованных.
 *
 * Отсутствие `permission`, `anyPermissions` и `role` означает «гейта нет
 * намеренно», и причина стоит рядом с
 * записью в `settingsGates.ts`. Такие пункты НЕ открывают группу (см.
 * `requiresGatedChild` в `shared/types/menu.ts`): они видны только когда группа
 * уже видна по другому, загейченному ребёнку.
 */
import { settingsGateFieldsFor } from './settingsGates';

export type SettingsMenuEntry = {
  /** Ключ i18n подписи. */
  labelKey: string;
  /** Ключ пункта меню = маршрут (или action-ключ, как `open-swagger`). */
  key: string;
  /** Право ЧТЕНИЯ API экрана. Выведено из `settingsGates.ts`, здесь не задаётся. */
  permission?: string;
  /** Любое из точных прав открывает пункт. */
  anyPermissions?: string[];
  /** Роль, если роут защищён ролью, а не правом (ProtectedRoute requiredRole). */
  role?: string;
};

/** Пункт меню = подпись + маршрут; гейт подтягивается из единого реестра. */
function entry(labelKey: string, key: string): SettingsMenuEntry {
  return { labelKey, key, ...settingsGateFieldsFor(key) };
}

export const SETTINGS_MENU_ENTRIES: readonly SettingsMenuEntry[] = [
  entry('menu:settings_main', '/settings'),
  entry('menu:settings_address', '/settings/address'),
  entry('menu:settings_equipment', '/settings/equipment'),
  entry('menu:settings_snmp_vendor_profiles', '/settings/snmp-vendor-profiles'),
  entry('menu:settings_poller', '/settings/poller'),
  entry('menu:settings_map', '/settings/map'),
  entry('menu:settings_modules', '/settings/modules'),
  entry('menu:settings_cron', '/settings/cron'),
  entry('menu:settings_notifications', '/settings/notifications'),
  entry('menu:settings_ldap', '/settings/ldap'),
  entry('menu:settings_billing', '/settings/modules/billing'),
  entry('menu:settings_documents', '/settings/documents'),
  entry('menu:settings_additional_services', '/settings/additional-services'),
  entry('menu:settings_node_types', '/settings/node-types'),
  entry('menu:settings_node_icons', '/settings/node-icons'),
  entry('menu:settings_cable_lines', '/settings/cable-lines'),
  entry('menu:settings_tasks', '/settings/tasks'),
  entry('menu:settings_observer', '/settings/observer'),
  // Слайс observer-admin.1 (каталог OID) — отдельный от `/settings/observer` выше, который
  // остаётся заглушкой-лендингом (наблюдение без backend, будущий observer-admin.6).
  entry('menu:settings_observer_oids', '/settings/observer/oids'),
  entry('menu:settings_observer_profiles', '/settings/observer/profiles'),
  entry('menu:settings_observer_devices', '/settings/observer/devices'),
  entry('menu:settings_observer_readings', '/settings/observer/readings'),
  entry('menu:settings_observer_log', '/settings/observer/log'),
  entry('menu:settings_warehouse', '/settings/warehouse'),
  entry('menu:settings_swagger', 'open-swagger'),
] as const;
